import * as d3 from 'd3';

import { getUrl, getIdSelector } from './utils';
import dompurify from 'dompurify';

export function drawItems(selection, attributes, { maskId, blueMaskId }) {
  attributes.items.forEach(item => {
    const { x, y, width, height, url, text, style, className } = item;

    if (url) {
      selection
        .append('image')
        .attr('x', Math.max(x, 20))
        .attr('y', y)
        .attr('xlink:href', url)
        .attr('mask', getUrl(maskId))
        .attr('width', width)
        .attr('height', height)
        .attr('style', style)
        .attr('class', className);
    }
    if (text) {
      selection
        .append('foreignObject')
        .attr('x', x)
        .attr('y', y)
        .attr('mask', getUrl(maskId))
        .attr('width', width)
        .attr('height', height)
        .attr('style', style)
        .attr('class', className)
        .text(dompurify.sanitize(text));
    }
  });
  selection
    .append('rect')
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', attributes.width)
    .attr('height', attributes.height)
    .attr('mask', getUrl(blueMaskId))
    .style('fill', '#336699')
    .style('fill-opacity', 1);

  return null;
}

export function drawClearedItems(selection, attributes) {
  return attributes.items.forEach(item => {
    const { x, y, width, height, url, text, style, className } = item;

    if (url) {
      selection
        .append('image')
        .attr('x', x)
        .attr('y', y)
        .attr('xlink:href', url)
        .attr('width', width)
        .attr('height', height)
        .attr('style', style)
        .attr('class', className);
    }
    if (text) {
      selection
        .append('foreignObject')
        .attr('x', x)
        .attr('y', y)
        .attr('width', width)
        .attr('height', height)
        .attr('style', style)
        .attr('class', className)
        .text(dompurify.sanitize(text));
    }
  });
}

export function createMasks(defs, masksIds) {
  return masksIds.map(maskId => defs.append('mask').attr('id', maskId));
}

export function createBlurs(defs, blurs) {
  return blurs.map(({ id, amount }) =>
    defs.append('filter').attr('id', id).append('feGaussianBlur').attr('stdDeviation', amount)
  );
}

export function drawMasks(attributes, { blueMask, mask }) {
  const { width, height } = attributes;

  mask
    .append('rect')
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', width)
    .attr('height', height)
    .style('fill', 'white')
    .style('opacity', 0.01);

  blueMask
    .append('rect')
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', width)
    .attr('height', height)
    .style('fill', '#336699')
    .style('opacity', 0.6);
}

export function drawBluredItems(selection, attributes, { imageBlur, textBlur }) {
  attributes.items.forEach((item, index) => {
    const { x, y, width, height, url, text, style, className } = item;

    if (url) {
      selection
        .append('image')
        .attr('x', Math.max(x, 20) + attributes.blurAmount)
        .attr('y', y + attributes.blurAmount)
        .attr('filter', getUrl(imageBlur.id))
        .attr('xlink:href', url)
        .attr('key', `$image${index}${width}${height}${x}${y}`)
        .attr('width', width - attributes.blurAmount * 2)
        .attr('height', height - attributes.blurAmount * 2)
        .style('fill', 'white');
    }

    if (text) {
      selection
        .append('foreignObject')
        .attr('x', x)
        .attr('y', y)
        .attr('filter', getUrl(textBlur.id))
        .attr('width', width)
        .attr('height', height)
        .attr('key', `$text${index}${width}${height}${x}${y}`)
        .append('xhtml:div')
        .attr('style', style)
        .attr('class', className)
        .style('fill', 'white')
        .text(dompurify.sanitize(text));
    }
  });

  return null;
}

export function addMasks({ x, y, circleRadius, blueMask, mask }) {
  mask
    .append('circle')
    .attr('cx', x)
    .attr('cy', y)
    .attr('r', circleRadius)
    .attr('fill', 'white')
    .attr('class', 'clipCircle');

  blueMask
    .append('circle')
    .attr('cx', x)
    .attr('cy', y)
    .attr('r', circleRadius)
    .attr('fill', 'black')
    .attr('class', 'clipCircle');

  return null;
}

export function clearBlur(selection, attributes, { blueMaskId, maskId }) {
  const { width, height, blurAmount } = attributes;

  const mask = selection.select(getIdSelector(maskId));
  const blueMask = selection.select(getIdSelector(blueMaskId));

  const blueRect = selection.select('rect[mask]');

  const bluredTexts = selection.selectAll('foreignObject[filter]');
  const bluredImages = selection.selectAll('foreignObject[filter]');

  bluredImages.transition().ease(d3.easeQuad).delay(0).duration(1000).style('opacity', 0);
  bluredTexts.transition().ease(d3.easeQuad).delay(0).duration(1000).style('opacity', 0);

  blueRect.transition().delay(1000).remove();

  mask
    .append('circle')
    .attr('cx', width / 2)
    .attr('cy', height / 2)
    .transition()
    .ease(d3.easeQuad)
    .delay(0)
    .duration(1000)
    .attr('r', Math.sqrt(width * width + height * height) / 2)
    .attr('fill', 'white')
    .attr('class', 'clipCircle');

  blueMask
    .append('circle')
    .attr('cx', width / 2)
    .attr('cy', height / 2)
    .transition()
    .ease(d3.easeQuad)
    .delay(0)
    .duration(1000)
    .attr('r', Math.sqrt(width * width + height * height) / 2 + blurAmount)
    .attr('fill', 'black')
    .attr('class', 'clipCircle');
}
